import moment from 'moment-timezone';
import _ from 'lodash';
import isPickableDate from '@client/utils/isPickableDate';
import { sendFailureNotification } from '@client/_blessed/store/utils/toastNotifications';
import { TIME_ZONES } from '@drayalliance/types';

export function formatDateRangeFilterValues(
  start,
  end,
  showTimezones = false,
  timezone = TIME_ZONES.AMERICA_LOS_ANGELES
) {
  const startStr = moment(start)
    .tz(timezone)
    .format(`YYYY/MM/DD HH:mm${showTimezones ? ' z' : ''}`);
  const endStr = moment(end)
    .tz(timezone)
    .format(`YYYY/MM/DD HH:mm${showTimezones ? ' z' : ''}`);

  return `${startStr} - ${endStr}`;
}

function getAppointmentStartInvalidDate(stopInstant, maxPickableDays) {
  const appointmentStart = _.get(stopInstant, 'attributes.appointmentStart');
  const { id } = stopInstant;
  if (!isPickableDate(appointmentStart, maxPickableDays)) {
    return {
      id,
      field: 'Appointment start date',
      invalidDate: appointmentStart
    };
  }
}

function getAppointmentEndInvalidDate(stopInstant, maxPickableDays) {
  const appointmentEnd = _.get(stopInstant, 'attributes.appointmentEnd');
  const { id } = stopInstant;
  if (!isPickableDate(appointmentEnd, maxPickableDays)) {
    return {
      id,
      field: 'Appointment end date',
      invalidDate: appointmentEnd
    };
  }
}

function getIngateAndOutgateInvalidDate(stopInstant, maxPickableDays) {
  const ingateOrOutgateDate = _.get(stopInstant, 'attributes.actualDateTime');
  const { id } = stopInstant;
  if (!isPickableDate(ingateOrOutgateDate, maxPickableDays)) {
    return {
      id,
      field: 'Ingate/Outgate date',
      invalidDate: ingateOrOutgateDate
    };
  }
}

function getInvalidDates(stopInstant, maxPickableDays) {
  const invalidDates = [
    getAppointmentStartInvalidDate(stopInstant, maxPickableDays),
    getAppointmentEndInvalidDate(stopInstant, maxPickableDays),
    getIngateAndOutgateInvalidDate(stopInstant, maxPickableDays)
  ];

  return _.reject(invalidDates, _.isNil);
}

function findInvalidDatesFromStopInstantPairs(stopInstantPairs, maxPickableDays) {
  return stopInstantPairs.map((stopInstantPair) => {
    const stopInstant = stopInstantPair[1];
    return getInvalidDates(stopInstant, maxPickableDays);
  });
}

function findInvalidDates(stopInstant, maxPickableDays) {
  const stopInstantPairs = _.toPairs(stopInstant);
  const nestedInvalidDates = findInvalidDatesFromStopInstantPairs(stopInstantPairs, maxPickableDays);
  return _.flatten(nestedInvalidDates);
}

function missingStopInstant(resourceUpdatesByType) {
  return !resourceUpdatesByType.stopInstant;
}

export function findInvalidStopInstantDates(resourceUpdatesByType, maxPickableDays) {
  if (missingStopInstant(resourceUpdatesByType)) {
    return [];
  }
  return findInvalidDates(resourceUpdatesByType.stopInstant, maxPickableDays);
}

/**
 * This function returns a boolean representing whether
 * all Legs being updated to status `driver_assigned`
 * either have a carrier already assigned or have a carrier included in the leg update
 * @param {object} legUpdatesByUuid
 * @param {object} legsById
 * @returns {boolean}
 */
function validateDriverAssignedStatusUpdates(legUpdatesByUuid, legsById) {
  const legUuids = Object.keys(legUpdatesByUuid);
  const driverAssignedLegUuids = legUuids.filter(
    (uuid) => legUpdatesByUuid[uuid].attributes.status === 'driver_assigned'
  );

  return driverAssignedLegUuids.every((uuid) => {
    const leg = legsById[uuid];
    const legUpdate = legUpdatesByUuid[uuid];

    return (leg.carrierUUID && legUpdate.attributes.carrierUuid !== null) || legUpdate.attributes.carrierUuid;
  });
}

/**
 * This function validates updates to Leg statuses
 * and returns a boolean representing whether the updates including Leg status updates meet requirements
 * @param {object} legUpdatesByUuid
 * @param {object} legsById
 * @returns {boolean}
 */
// eslint-disable-next-line @typescript-eslint/default-param-last
export function validateLegStatusUpdates(legUpdatesByUuid = {}, legsById) {
  const driverAssignedUpdatesAreValid = validateDriverAssignedStatusUpdates(legUpdatesByUuid, legsById);

  if (!driverAssignedUpdatesAreValid) {
    sendFailureNotification('Cannot change leg status to Driver Assigned without assigning a carrier');
  }

  return driverAssignedUpdatesAreValid;
}

/**
 * Removes a single pending field edit, pruning any resource or resource-type group it leaves empty.
 * Mutates and returns `resourceUpdatesByType`.
 */
export function removePendingResourceUpdate(resourceUpdatesByType, { id, name, resourceType }) {
  const resourceGroup = resourceUpdatesByType[resourceType];
  const resource = resourceGroup?.[id];

  if (!resource) {
    return resourceUpdatesByType;
  }

  delete resource.attributes[name];

  if (_.isEmpty(resource.attributes)) {
    delete resourceGroup[id];
  }

  if (_.isEmpty(resourceGroup)) {
    delete resourceUpdatesByType[resourceType];
  }

  return resourceUpdatesByType;
}
